import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './RaiseTicket.css';

const RaiseTicket = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    license_key: '',
    subject: '',
    description: '',
    priority: 'medium',
    category: '',
    customer_name: '',
    customer_email: '',
    customer_phone: ''
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.license_key || !formData.subject || !formData.description || 
          !formData.customer_name || !formData.customer_email) {
        setError('Please fill in all required fields.');
        setLoading(false);
        return;
      }

      const response = await api.createSupportTicket(formData, files);
      
      if (response.success) {
        setSuccess(true);
        setSubmittedTicket(response.data);
        // Reset form
        setFormData({
          license_key: '',
          subject: '',
          description: '',
          priority: 'medium',
          category: '',
          customer_name: '',
          customer_email: '',
          customer_phone: ''
        });
        setFiles([]);
      }
    } catch (error) {
      setError(error.message || 'Failed to submit support ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success && submittedTicket) {
    return (
      <div className="raise-ticket-container">
        <div className="raise-ticket-card success-card">
          <div className="success-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <h2>Ticket Submitted Successfully!</h2>
          <div className="ticket-info">
            <p><strong>Ticket Number:</strong> <code>{submittedTicket.ticket_number}</code></p>
            <p><strong>Subject:</strong> {submittedTicket.subject}</p>
            <p className="success-message">
              Thank you for contacting us. We have received your support ticket and will respond as soon as possible.
              Please save your ticket number for reference.
            </p>
          </div>
          <div className="button-group">
            <button className="btn-primary" onClick={() => { setSuccess(false); setSubmittedTicket(null); }}>
              Submit Another Ticket
            </button>
            <button className="btn-secondary" onClick={() => navigate('/')}>
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="raise-ticket-container">
      <div className="raise-ticket-card">
        <div className="card-header">
          <h1><i className="fas fa-ticket-alt"></i> Raise Support Ticket</h1>
          <p className="subtitle">Submit a support request and our team will assist you</p>
        </div>

        {error && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="ticket-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="license_key">
                License Key <span className="required">*</span>
              </label>
              <input
                type="text"
                id="license_key"
                name="license_key"
                value={formData.license_key}
                onChange={handleChange}
                placeholder="Enter your license key"
                required
                className="form-control"
              />
              <small className="form-help">Enter the license key associated with your account</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="subject">
                Subject <span className="required">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Brief description of the issue"
                required
                className="form-control"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="description">
                Description <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Please provide detailed information about your issue..."
                required
                rows="6"
                className="form-control"
              ></textarea>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select a category (optional)</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing Question</option>
                <option value="feature_request">Feature Request</option>
                <option value="bug_report">Bug Report</option>
                <option value="general">General Inquiry</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="form-control"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer_name">
                Your Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="customer_name"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="customer_email">
                Email Address <span className="required">*</span>
              </label>
              <input
                type="email"
                id="customer_email"
                name="customer_email"
                value={formData.customer_email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
                className="form-control"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer_phone">Phone Number</label>
              <input
                type="tel"
                id="customer_phone"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                placeholder="+1234567890 (optional)"
                className="form-control"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="attachments">Attachments (Optional)</label>
              <input
                type="file"
                id="attachments"
                name="attachments"
                onChange={handleFileChange}
                multiple
                className="form-control file-input"
              />
              <small className="form-help">
                Maximum file size: 10MB per file. Supported formats: Images, PDF, Word, Text files.
              </small>

              {files.length > 0 && (
                <div className="file-list">
                  <strong>Selected Files:</strong>
                  <ul>
                    {files.map((file, index) => (
                      <li key={index}>
                        <i className="fas fa-paperclip"></i> {file.name}
                        <span className="file-size">
                          ({(file.size / 1024).toFixed(2)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          className="remove-file-btn"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i> Submit Ticket
                </>
              )}
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate('/')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RaiseTicket;

